// SEO metadata per supported language for the landing page.
// Used by useLocalizedSeo hook to set <title>, meta description, OG/Twitter
// tags and hreflang alternates.
//
// NOTE: Each language now has its own canonical origin (ccTLD) — see
// `src/lib/domain-config.ts`. This file only owns the *copy* (titles,
// descriptions, og:locale). Origins / paths come from domain-config.

export type SeoLang = "da" | "en" | "nb" | "sv" | "de" | "fr" | "es" | "pt";

export interface SeoMeta {
  title: string;
  description: string;
  ogLocale: string; // BCP 47 with underscore (e.g. da_DK)
}

// Language → URL path within the *current* host. Each ccTLD serves its
// language at "/"; relark.app uses path prefixes for ES/FR/PT.
// Re-exported from domain-config so legacy callers keep working.
export { PATH_BY_LANG as LANG_PATHS, ORIGIN_BY_LANG } from "@/lib/domain-config";

/**
 * @deprecated Use `originFor(lang)` from `@/lib/domain-config` — the site
 * no longer has a single origin. Kept as the Danish ccTLD because that is
 * the historical primary domain.
 */
export const SITE_ORIGIN = "https://relark.dk";

export const SEO_BY_LANG: Record<SeoLang, SeoMeta> = {
  da: {
    title: "Relark — Den psykologiske copilot for nordiske mellemledere",
    description:
      "Sparringspartneren der altid er klar. Beskriv en svær situation på 30 sek og få et konkret samtalemanuskript + bias-tjek på under 5 minutter. Bygget på Big Five-forskning. Hostet i Paris. EU AI Act-klar.",
    ogLocale: "da_DK",
  },
  en: {
    title: "Relark — The psychological copilot for Nordic middle managers",
    description:
      "The sparring partner that's always ready. Describe a difficult situation in 30 sec and get a concrete conversation script + bias check in under 5 minutes. Built on Big Five research. Hosted in Paris. EU AI Act-ready.",
    ogLocale: "en_US",
  },
  nb: {
    title: "Relark — Den psykologiske copiloten for nordiske mellomledere",
    description:
      "Sparringspartneren som alltid er klar. Beskriv en vanskelig situasjon på 30 sek og få et konkret samtaleskript + bias-sjekk på under 5 minutter. Bygget på Big Five-forskning. Hostet i Paris. EU AI Act-klar.",
    ogLocale: "nb_NO",
  },
  sv: {
    title: "Relark — Den psykologiska copiloten för nordiska mellanchefer",
    description:
      "Sparringspartnern som alltid är redo. Beskriv en svår situation på 30 sek och få ett konkret samtalsmanus + bias-kontroll på under 5 minuter. Byggd på Big Five-forskning. Hostad i Paris. EU AI Act-redo.",
    ogLocale: "sv_SE",
  },
  de: {
    title: "Relark — Der psychologische Copilot für nordische Führungskräfte",
    description:
      "Der Sparringspartner, der immer bereit ist. Beschreiben Sie eine schwierige Situation in 30 Sek. und erhalten Sie ein konkretes Gesprächsskript + Bias-Check in unter 5 Minuten. Basierend auf Big-Five-Forschung. In Paris gehostet. EU-AI-Act-konform.",
    ogLocale: "de_DE",
  },
  fr: {
    title: "Relark — Le copilote psychologique pour les managers intermédiaires nordiques",
    description:
      "Le partenaire de sparring toujours prêt. Décrivez une situation difficile en 30 sec et obtenez un script de conversation concret + un contrôle de biais en moins de 5 minutes. Basé sur la recherche Big Five. Hébergé à Paris. Conforme EU AI Act.",
    ogLocale: "fr_FR",
  },
  es: {
    title: "Relark — El copiloto psicológico para mandos intermedios nórdicos",
    description:
      "El compañero de sparring siempre listo. Describe una situación difícil en 30 seg y obtén un guion de conversación concreto + un chequeo de sesgos en menos de 5 minutos. Basado en investigación Big Five. Alojado en París. Conforme con EU AI Act.",
    ogLocale: "es_ES",
  },
  pt: {
    title: "Relark — O copiloto psicológico para gestores intermédios nórdicos",
    description:
      "O parceiro de sparring sempre pronto. Descreva uma situação difícil em 30 seg e obtenha um guião de conversa concreto + verificação de viés em menos de 5 minutos. Baseado na investigação Big Five. Alojado em Paris. Conforme EU AI Act.",
    ogLocale: "pt_PT",
  },
};

export const SUPPORTED_SEO_LANGS = Object.keys(SEO_BY_LANG) as SeoLang[];
